import { useId } from 'react';
import { Alert, Button, LoadingSpinner, Modal } from '@shared/components';

export type DocumentViewerStatus = 'loading' | 'ready' | 'error';

interface Props {
    isOpen: boolean;
    title: string;
    subtitle?: string;
    status: DocumentViewerStatus;
    fileUrl?: string;
    errorMessage?: string;
    downloadError?: string;
    canDownload: boolean;
    isDownloading?: boolean;
    onDownload: () => void;
    onClose: () => void;
}

export function DocumentViewerModal({ isOpen, title, subtitle, status, fileUrl, errorMessage, downloadError, canDownload, isDownloading = false, onDownload, onClose }: Props) {
    const headingId = useId();

    return (
        <Modal isOpen={isOpen} onClose={onClose} ariaLabelledBy={headingId} className="w-full max-w-5xl p-6">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3 pr-8">
                <div>
                    <h2 id={headingId} className="text-lg font-semibold">{title}</h2>
                    {subtitle && <p className="text-sm text-mp-muted">{subtitle}</p>}
                </div>
                {canDownload && status === 'ready' && (
                    <Button type="button" size="sm" variant="primary" disabled={isDownloading} onClick={onDownload}>
                        {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                )}
            </div>
            {downloadError && <Alert variant="error" className="mb-4">{downloadError}</Alert>}
            <ViewerBody status={status} title={title} fileUrl={fileUrl} errorMessage={errorMessage} />
        </Modal>
    );
}

function ViewerBody({ status, title, fileUrl, errorMessage }: Pick<Props, 'status' | 'title' | 'fileUrl' | 'errorMessage'>) {
    if (status === 'loading') {
        return (
            <div className="flex h-96 items-center justify-center rounded-lg border border-mp-border bg-mp-background">
                <LoadingSpinner message="Loading document" />
            </div>
        );
    }

    if (status === 'error' || !fileUrl) {
        return <Alert variant="error">{errorMessage ?? 'The document could not be loaded.'}</Alert>;
    }

    return <iframe title={title} src={fileUrl} className="h-[70vh] w-full rounded-lg border border-mp-border bg-mp-background" />;
}
